import { useEffect, useRef, useState } from "react"
import { HexColorPicker } from "react-colorful"
import { TrashIcon } from "components/icons"
import TagV2 from "components/v2/components/common/TagV2"

interface ColorPickerButtonProps {
    bgColor?: string
    onChange: (color: string) => void
    onReset?: () => void
}

const isValidHex = (color: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color)

const ColorPickerButton: React.FC<ColorPickerButtonProps> = ({ bgColor, onChange, onReset }) => {
    const [inputColor, setInputColor] = useState(bgColor || "#ffffff00")
    const [isOpen, setIsOpen] = useState(false)
    const [isValid, setIsValid] = useState(true)
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) {
            setInputColor(bgColor || "#ffffff00")
        }
    }, [bgColor, isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                if (inputColor !== bgColor && isValidHex(inputColor)) {
                    onChange(inputColor)
                }
            }
        }

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside)
        } else {
            document.removeEventListener("mousedown", handleClickOutside)
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [isOpen, inputColor, bgColor, onChange])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setInputColor(value)
        setIsValid(isValidHex(value))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (e.key === "Enter") {
            e.preventDefault()
            if (isValidHex(inputColor)) {
                onChange(inputColor)
                setIsOpen(false)
            } else {
                setIsValid(false)
            }
        }
    }

    const handleReset = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const defaultColor = "#ffffff00"
        setInputColor(defaultColor)
        setIsValid(true)
        if (onReset) onReset()
        setIsOpen(false)
    }

    const formatColorForDisplay = (color: string) => {
        if (!color) return "#ffffff00"
        const cleanColor = color.replace(/[^0-9A-Fa-f#]/g, "")
        return cleanColor.startsWith("#") ? cleanColor : `#${cleanColor}`
    }

    return (
        <div className="relative" ref={popoverRef}>
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    className="flex items-center gap-4"
                    onClick={() => {
                        setIsOpen((prev) => !prev)
                        setIsValid(true)
                    }}
                >
                    <div
                        className="relative w-[40px] h-[40px] flex items-center rounded-md py-[9.5px] px-[10.5px] border border-base-light p-0"
                        style={{ backgroundColor: formatColorForDisplay(bgColor) }}
                    />
                    <div className="px-6 rounded-md py-2 leading-5 text-[#9CA3AF] border border-base-light font-normal text-base min-w-[130px]">
                        {formatColorForDisplay(bgColor || "#ffffff00")}
                    </div>
                </button>
                {onReset && (
                    <TagV2 icon={<TrashIcon className="hover:bg-gray-100 text-gray-400" />} onClick={handleReset} />
                )}
            </div>

            {isOpen && (
                <div className="absolute bottom-full left-0 z-10 mb-2 bg-white rounded-lg shadow-lg p-4">
                    <div className="custom-color-picker mb-3 rounded">
                        <HexColorPicker
                            color={formatColorForDisplay(inputColor)}
                            onChange={(color) => {
                                setInputColor(color)
                                setIsValid(true)
                            }}
                        />
                    </div>

                    <div className="flex gap-2 w-full">
                        <input
                            type="text"
                            value={formatColorForDisplay(inputColor)}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            className={`px-4 py-2 leading-5 w-full text-[#374151] font-normal text-base rounded-md focus:outline-none focus:ring ${
                                isValid
                                    ? "border border-base-light focus:ring-blue-300"
                                    : "border border-red-400 focus:ring-red-300"
                            }`}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default ColorPickerButton
